// src/components/vendor/StoreCard.tsx

import React, { useState } from 'react';
import {
    Store, Edit, CheckCircle, Clock, XCircle,
    Globe, MapPin, Phone, Mail, Building2,
    AlertCircle, Upload,
    Check,
    Copy
} from 'lucide-react';
import type { Store as StoreType } from '../../types';
import { formatPhoneNumber } from '../../utils/phoneFormatter';

interface StoreCardProps {
    store: StoreType;
    onEdit: () => void;
    isProcessing?: boolean;
}

const StoreCard: React.FC<StoreCardProps> = ({ store, onEdit, isProcessing = false }) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [copied, setCopied] = useState(false);

    const getStatusBadge = () => {
        switch (store.verification_status) {
            case 'approved':
                return {
                    icon: CheckCircle,
                    label: 'Approved',
                    className: 'bg-green-100 text-green-800 border-green-200'
                };
            case 'pending':
                return {
                    icon: Clock,
                    label: 'Pending Review',
                    className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
                };
            case 'rejected':
                return {
                    icon: XCircle,
                    label: 'Rejected',
                    className: 'bg-red-100 text-red-800 border-red-200'
                };
            default:
                return {
                    icon: AlertCircle,
                    label: 'Draft',
                    className: 'bg-gray-100 text-gray-600 border-gray-200'
                };
        }
    };

    const handleCopy = (text: string | undefined | null) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const status = getStatusBadge();
    const StatusIcon = status.icon;

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gray-50/50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {/* Store Logo */}
                        <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 flex-shrink-0">
                            {store.logo ? (
                                <img
                                    src={store.logo}
                                    alt={store.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <Store className="h-8 w-8 text-gray-400" />
                            )}
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {store.name || 'Untitled Store'}
                            </h3>
                            {store.description && (
                                <p className="text-sm text-gray-500">{store.description}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Status Badge */}
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${status.className}`}>
                            <StatusIcon className="h-3.5 w-3.5" />
                            {status.label}
                        </span>

                        <button
                            onClick={onEdit}
                            disabled={isProcessing}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Edit Store"
                        >
                            <Edit className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
                {/* Store Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {store.store_address_detail && (
                        <div className="flex items-start gap-2 text-sm text-gray-600">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span>
                                {store.store_address_detail?.house_number && `${store.store_address_detail.house_number} `}
                                {store.store_address_detail?.address_line_1}
                                {store.store_address_detail?.address_line_2 && `, ${store.store_address_detail?.address_line_2}`}
                                <br />
                                {store.store_address_detail?.city_details.name}
                            </span>
                        </div>
                    )}

                    {store.contact_email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <a href={`mailto:${store.contact_email}`} className="hover:text-blue-600">
                                {store.contact_email}
                            </a>
                        </div>
                    )}

                    {store.contact_phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <a href={`tel:${store.contact_phone}`} className="hover:text-blue-600">
                                {formatPhoneNumber(store.contact_phone)}
                            </a>
                        </div>
                    )}


                    {store.website && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <a
                                href={store.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-blue-600 truncate"
                            >
                                {store.website.replace(/^https?:\/\//, '')}
                            </a>
                            <button
                                onClick={() => handleCopy(store.website)}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                                title="Copy website link"
                            >
                                {copied ? (
                                    <span className="text-xs text-green-600 flex items-center gap-1">
                                        <Check className="h-3.5 w-3.5 text-green-500" />
                                        Copied!
                                    </span>
                                ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Business Type & Registration */}
                {(store.business_type || store.registration_number) && (
                    <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-100">
                        {store.business_type && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Building2 className="h-4 w-4 text-gray-400" />
                                <span className="capitalize">{store.business_type.replace('_', ' ')}</span>
                            </div>
                        )}
                        {store.registration_number && (
                            <span className="text-sm text-gray-500">
                                Reg: {store.registration_number}
                            </span>
                        )}
                        {store.tax_id && (
                            <span className="text-sm text-gray-500">
                                Tax ID: {store.tax_id}
                            </span>
                        )}
                    </div>
                )}

                {/* Rejection Reason */}
                {store.verification_status === 'rejected' && store.rejection_reason && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-red-800">Rejection Reason</p>
                                <p className="text-sm text-red-700">{store.rejection_reason}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pending Notice */}
                {store.verification_status === 'pending' && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <Clock className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-yellow-800">Store Under Review</p>
                                <p className="text-sm text-yellow-700">
                                    Your store is being reviewed by our team. You'll receive an update once it's approved.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Documents Section */}
                {store.verification_status === 'pending' && store.documents && (
                    <div className="pt-3 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-2">Submitted Documents</p>
                        <div className="flex flex-wrap gap-2">
                            {store.documents.map((doc: any, index: number) => (
                                <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                                    <Upload className="h-3 w-3" />
                                    {doc.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StoreCard;